import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAddWardRequest,
  IUpdateWardConfigurationRequest,
  IUpdateWardRequest,
  IWard,
  TWardConfigurationResponse,
} from "../../../@types/admin/AdminWard";
import { wardsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export function useAddWard() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<IWard, Error, IAddWardRequest>(
    {
      mutationFn: async (request) =>
        ensureOk(await wardsService.addWard(request)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["admin-wards"] });
      },
    }
  );

  return { addWard: mutateAsync, isAddingWard: isPending };
}

export function useUpdateWard() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IWard,
    Error,
    IUpdateWardRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await wardsService.updateWard(request)),
    onSuccess: (_data, request) => {
      queryClient.invalidateQueries({ queryKey: ["admin-wards"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-ward-details", request.id],
      });
    },
  });

  return { updateWard: mutateAsync, isUpdatingWard: isPending };
}

export function useUpdateWardConfiguration(wardId: number) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    TWardConfigurationResponse,
    Error,
    IUpdateWardConfigurationRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await wardsService.updateWardConfiguration(wardId, request)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-wards"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-ward-details", wardId],
      });
    },
  });

  return {
    updateWardConfiguration: mutateAsync,
    isUpdatingWardConfiguration: isPending,
  };
}
